// FPS, frame time, tick time, position and velocity. Text is refreshed 4 times a second.

import type { WebGLRenderer } from 'three';
import { horizontalSpeed } from '../sim/player';
import type { Simulation } from '../sim/simulation';

const WINDOW_MS = 250;
/**
 * A tick takes a few microseconds but browsers round performance.now() to ~0.1 ms (5 µs only with
 * cross-origin isolation), so tick time is averaged over a longer window to be meaningful.
 */
const TICK_WINDOW_MS = 2000;
const HITCH_WINDOW_MS = 10000;
/** Spikes are listed for this long, at most SPIKES of them (newest first). */
const SPIKE_KEEP_MS = 120000;
const SPIKES = 5;

interface Spike {
  at: number;
  /** JavaScript memory dropped during the frame: a garbage collection ran (MB freed). */
  freed: number;
  /** What the game was doing just before (stage, respawn, music...). */
  notes: string;
  ms: number;
  sim: number;
  render: number;
  compiled: number;
  uploaded: number;
}

const f1 = (v: number) => v.toFixed(1);
const f2 = (v: number) => v.toFixed(2);
const vec = (v: readonly number[]) => f1(v[0]).padStart(9) + f1(v[1]).padStart(9) + f1(v[2]).padStart(9);

export class DebugOverlay {
  private visible = false;
  private windowStart = performance.now();
  private frames = 0;
  private frameMax = 0;
  private cpuSum = 0;

  private tickWindowStart = performance.now();
  private ticks = 0;
  private tickMs = 0;
  private batchMax = 0;
  private tickText = 'tick    measuring…';
  /** Worst frame of the last HITCH_WINDOW_MS, with what it spent its time on. */
  private readonly hitch = { ms: 0, sim: 0, render: 0, at: 0, compiled: 0, uploaded: 0 };
  /** Shader programs, and textures + geometries, on the GPU last frame (more: a compile or upload). */
  private programs = 0;
  private gpuItems = 0;
  private lastSimMs = 0;
  private lastRenderMs = 0;
  /** Typical frame time (slow average), to tell a spike from a steady low frame rate. */
  private typical = 0;
  /** The last few spikes: frames much longer than typical (newest first). */
  private readonly spikes: Spike[] = [];
  /** Things that happened lately (stage, respawn, new music track), for the spike lines. */
  private readonly notes: { at: number; what: string }[] = [];
  /** JavaScript heap at the last frame (Chrome; 0 elsewhere). */
  private heap = 0;

  constructor(
    private readonly el: HTMLElement,
    private readonly renderer: WebGLRenderer,
  ) {}

  setVisible(v: boolean) {
    this.visible = v;
    this.el.hidden = !v;
  }

  /** The usual frame time lately (ms; 0 until known). */
  get typicalFrame() {
    return this.typical;
  }

  get isVisible() {
    return this.visible;
  }

  /** Something happened (a stage, a respawn, a music track): spikes right after it say so. */
  note(what: string) {
    const now = performance.now();
    this.notes.push({ at: now, what });
    while (this.notes.length && now - this.notes[0].at > 1000) this.notes.shift();
  }

  /** Time spent drawing this frame. */
  recordRender(ms: number) {
    this.lastRenderMs = ms;
  }

  /** Record one frame's simulation batch: `n` ticks took `ms` in total. */
  recordTicks(n: number, ms: number) {
    this.lastSimMs = ms;
    if (n === 0) return;
    this.ticks += n;
    this.tickMs += ms;
    this.batchMax = Math.max(this.batchMax, ms);
  }

  /**
   * Call once per frame at the end of the frame.
   * `frameMs` is the time since the previous frame, `cpuMs` the time spent in this frame's work.
   */
  frame(now: number, frameMs: number, cpuMs: number, sim: Simulation, rawInput: boolean) {
    this.frames++;
    this.frameMax = Math.max(this.frameMax, frameMs);
    const info = this.renderer.info;
    const programs = info.programs?.length ?? 0;
    const gpuItems = info.memory.textures + info.memory.geometries;
    const compiled = Math.max(0, programs - this.programs);
    const uploaded = Math.max(0, gpuItems - this.gpuItems);
    this.programs = programs;
    this.gpuItems = gpuItems;
    if (frameMs > this.hitch.ms || now - this.hitch.at > HITCH_WINDOW_MS) {
      const h = this.hitch;
      h.ms = frameMs;
      h.sim = this.lastSimMs;
      h.render = this.lastRenderMs;
      h.at = now;
      h.compiled = compiled;
      h.uploaded = uploaded;
    }
    // Chrome reports the JavaScript heap: a drop since the last frame means a garbage collection ran.
    const heap =
      (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize ?? 0;
    const freed = Math.max(0, this.heap - heap);
    this.heap = heap;
    // A spike: well over the usual frame and long enough to feel (one at 165 Hz is ~6 ms).
    if (this.typical > 0 && frameMs > Math.max(12, this.typical * 1.8) && frameMs < 1000) {
      let notes = '';
      for (const n of this.notes)
        if (now - n.at < frameMs + 300 && !notes.includes(n.what)) notes += ' ' + n.what;
      this.spikes.unshift({
        at: now,
        ms: frameMs,
        sim: this.lastSimMs,
        render: this.lastRenderMs,
        compiled,
        uploaded,
        freed: freed / 1048576,
        notes: notes.trim(),
      });
      if (this.spikes.length > SPIKES) this.spikes.length = SPIKES;
    }
    // Big gaps (a hidden tab, the menu) don't count toward the typical frame.
    if (frameMs < 100) this.typical = this.typical ? this.typical + (frameMs - this.typical) * 0.02 : frameMs;
    this.lastSimMs = 0;
    this.cpuSum += cpuMs;

    const tickElapsed = now - this.tickWindowStart;
    if (tickElapsed >= TICK_WINDOW_MS) {
      this.tickText = this.ticks
        ? `tick    ${f1((this.tickMs / this.ticks) * 1000)} µs avg  ${f2(this.batchMax)} ms max/frame  ` +
          `(${Math.round((this.ticks * 1000) / tickElapsed)}/s)`
        : 'tick    paused';
      this.tickWindowStart = now;
      this.ticks = 0;
      this.tickMs = 0;
      this.batchMax = 0;
    }

    const elapsed = now - this.windowStart;
    if (elapsed < WINDOW_MS) return;
    if (this.visible) {
      const p = sim.player;
      const draw = info.render;
      this.el.textContent =
        `fps     ${f1((this.frames * 1000) / elapsed)}\n` +
        `frame   ${f2(elapsed / this.frames)} ms  max ${f2(this.frameMax)}\n` +
        `cpu     ${f2(this.cpuSum / this.frames)} ms / frame\n` +
        `${this.tickText}\n` +
        `pos  ${vec(p.pos)}\n` +
        `vel  ${vec(p.vel)}\n` +
        `speed   ${f1(horizontalSpeed(p))} u/s  ${p.ground ? 'ground' : 'air'}${p.ducked ? ' ducked' : ''}\n` +
        `draws   ${draw.calls}  tris ${draw.triangles}\n` +
        `mouse   ${rawInput ? 'raw' : 'os-accelerated'}\n` +
        `hitch   ${f1(this.hitch.ms)} ms worst in 10 s (sim ${f2(this.hitch.sim)}, draw ${f2(this.hitch.render)}, ` +
        `other ${f1(Math.max(0, this.hitch.ms - this.hitch.sim - this.hitch.render))})` +
        (this.hitch.compiled ? ` · ${this.hitch.compiled} shader compiled` : '') +
        (this.hitch.uploaded ? ` · ${this.hitch.uploaded} uploaded` : '') +
        this.spikeText(now);
    }
    this.windowStart = now;
    this.frames = 0;
    this.frameMax = 0;
    this.cpuSum = 0;
  }

  /** The spikes of the last two minutes, one line each. */
  private spikeText(now: number): string {
    while (this.spikes.length && now - this.spikes[this.spikes.length - 1].at > SPIKE_KEEP_MS)
      this.spikes.pop();
    if (!this.spikes.length) return '\nspikes  none in 2 min';
    let t = '\nspikes  (last 2 min, newest first)';
    for (const k of this.spikes)
      t +=
        `\n  ${f1(k.ms)} ms ${Math.round((now - k.at) / 1000)} s ago: sim ${f2(k.sim)}, draw ${f2(k.render)}, ` +
        `other ${f1(Math.max(0, k.ms - k.sim - k.render))}` +
        (k.compiled ? ` · ${k.compiled} shader compiled` : '') +
        (k.uploaded ? ` · ${k.uploaded} uploaded` : '') +
        (k.freed >= 0.5 ? ` · GC freed ${f1(k.freed)} MB` : '') +
        (k.notes ? ` · after ${k.notes}` : '');
    return t;
  }
}
